import { service } from '@ember/service';

import type { RecordData, Service } from 'webapp-types';
import { ModelName, UserLevel } from 'webapp/enums/enums';
import { capitalize } from 'webapp/utils/common';
import { getFormattedTime, getRelativeTime } from 'webapp/utils/number';

import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import type UserRecord from 'webapp/models/user';

import type { UserLevelOption } from 'webapp/services/constants';

export interface Metadata {
	title: string | null;
	department: string | null;
	role: string | null;
	seniority: string | null;
	personaType: PersonaType | null;
	onboardingObjectives: string[] | null; // hidden for now, hidden-onboardingObjectives
	sourcesSelection: string[] | null;
	onboardingObjectivesSub: string | null; // hidden for now, hidden-onboardingObjectives
}

export enum PersonaType {
	ENABLER = 'Enabler',
	RECEIVER = 'Receiver',
}

export default class User extends BufferedModel<UserRecord, RecordData.User.Serialized> {
	@service('account') declare accountService: Service.Account;
	@service('constants') declare constantsService: Service.Constants;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;

	// ------- props from / to ED record ------

	get id() {
		return this.model.id;
	}

	get name(): string {
		return this.model.name;
	}

	get email(): string {
		return this.model.email;
	}

	get avatar(): string {
		return this.model.avatar;
	}

	get activated(): boolean {
		return this.model.activated;
	}

	get isVerified(): boolean {
		return this.model.isVerified;
	}

	get created(): number {
		return this.model.created;
	}

	get lastSeen(): number {
		return this.model.lastSeen;
	}

	get lastLoginMobile(): number {
		return this.model.lastLoginMobile;
	}

	get level(): UserLevel {
		return this.model.level;
	}

	get timezone(): string {
		return this.model.timezone;
	}

	get notifications(): number[] {
		return this.model.notifications;
	}

	set notifications(value: number[]) {
		this.model.notifications = value;
	}

	get spaces(): number[] {
		return this.model.spaces;
	}

	get isRestricted(): boolean {
		return this.model.isRestricted;
	}

	get connectedSlack(): boolean {
		return this.model.connectedSlack;
	}

	get resendInvitationMailCount(): number {
		return this.model.resendInvitationMailCount;
	}

	get metadata(): Metadata {
		return this.getBuffered('metadata');
	}

	set metadata(value: Metadata) {
		this.setBuffered('metadata', value);
	}

	// ------- other props ------

	override get validationRules() {
		return {
			name: ['required'],
			email: ['required', 'email'],
		};
	}

	get isMe() {
		return this.currentUserService.id === this.id;
	}

	get isAccountOwner() {
		return this.accountService.ownerId === this.numericId;
	}

	get isAdmin() {
		return this.level === UserLevel.ADMIN;
	}

	get isEditor() {
		return this.level === UserLevel.EDITOR;
	}

	get isUser() {
		return this.level === UserLevel.USER;
	}

	get isViewer() {
		return this.level === UserLevel.VIEWER;
	}

	get isDemo() {
		return this.level === UserLevel.DEMO;
	}

	get isParentUser() {
		return this.model.isParentUser;
	}

	get canResendInvitationMail() {
		return (
			!this.isRestricted &&
			!!this.mmUsersService.inactiveInvitedUserRecords.find((u) => u.id === this.id)
		);
	}

	get levelInfo(): UserLevelOption {
		return this.constantsService.userLevels.find((lvl) => lvl.value === this.level)!;
	}

	get roleText() {
		if (this.isAccountOwner) {
			return 'Account owner';
		} else if (this.isDemo) {
			return 'Demo';
		} else if (this.isParentUser) {
			return 'Admin (organization)';
		} else {
			return this.levelInfo.label;
		}
	}

	get levelText() {
		return this.levelInfo.text!;
	}

	get joinedText() {
		return getFormattedTime(this.created, this.currentUserService.model?.timezone);
	}

	get lastSeenText() {
		return this.lastSeen ? getRelativeTime(this.lastSeen) : '-';
	}

	get capitalizedName() {
		return capitalize(this.name);
	}

	get firstName() {
		return this.mmUsersService.getFirstName(this.name);
	}

	get allowedBoards() {
		return this.mmBoardsService.normalBoardModels.filter((board) => {
			return board.users.includes(this.numericId!);
		});
	}

	static override modelName = ModelName.USER; // store record type
}
